import React, { useEffect, useState } from "react";
import styled from "styled-components";
import {
  LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer, Legend
} from "recharts";
import { Info } from "lucide-react";
import { Tooltip as InfoTooltip } from "react-tooltip";

const Wrapper = styled.div`
  width: 100%;
  padding: 20px;
`;

const ChartTitle = styled.div`
  font-family: Arial, sans-serif;
  font-size: 12px;
  font-style: normal;
  font-weight: bold;
  color: #666;
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
`;

const FilterGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const InfoIconWrapper = styled.div`
  cursor: pointer;
  color: #666;
  transition: color 0.3s;
  &:hover { color: #000; }
  svg { width: 18px; height: 18px; }
`;

const Controls = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 20px;
  gap: 8px;
  flex-wrap: wrap;
`;

const Select = styled.select`
  padding: 8px 12px;
  font-size: 15px;
  border: 1px solid #ccc;
  border-radius: 4px;
`;

const SalesChangesByRegion = ({ salesData = [], clientsData = [] }) => {
  const [filteredData, setFilteredData] = useState([]);
  const [availableYears, setAvailableYears] = useState([]);
  const [selectedYear, setSelectedYear] = useState("");
  const [hiddenRegions, setHiddenRegions] = useState(new Set());

  const monthNames = [
    "Styczeń", "Luty", "Marzec", "Kwiecień", "Maj", "Czerwiec", 
    "Lipiec", "Sierpień", "Wrzesień", "Październik", "Listopad", "Grudzień"
  ];

  const regionNameMapping = {
    MAZOWIECKIE: "mazowieckie",
    ŚLĄSKIE: "śląskie",
    PODKARPACKIE: "podkarpackie",
    LUBUSKIE: "lubuskie",
    ZACHODNIOPOMORSKIE: "zachodniopomorskie",
    WIELKOPOLSKIE: "wielkopolskie",
    DOLNOŚLĄSKIE: "dolnośląskie",
    ŁÓDZKIE: "łódzkie",
    ŚWIĘTOKRZYSKIE: "świętokrzyskie",
    POMORSKIE: "pomorskie",
    LUBELSKIE: "lubelskie",
    OPOLSKIE: "opolskie",
    PODLASKIE: "podlaskie",
    "WARMIŃSKO-MAZURSKIE": "warmińsko-mazurskie",
    "KUJAWSKO-POMORSKIE": "kujawsko-pomorskie",
    MAŁOPOLSKIE: "małopolskie",
  };

  useEffect(() => {
    if (salesData.length === 0 || clientsData.length === 0) return;

    const years = [...new Set(salesData.map((row) => row.Rok.toString()))].sort();
    setAvailableYears(years);
    setSelectedYear(years.includes("2024") ? "2024" : years[years.length - 1]);
  }, [salesData, clientsData]);

  useEffect(() => {
    if (!selectedYear || salesData.length === 0 || clientsData.length === 0) return;

    const kontrahentaToRegion = {};
    salesData.forEach((row) => {
      const client = clientsData.find(
        (c) => c.nip?.trim() === row.NrKontrahenta?.trim()
      );
      if (client) {
        const normalizedRegion = client.wojewodztwo?.trim().toUpperCase();
        kontrahentaToRegion[row.NrKontrahenta?.trim()] =
          regionNameMapping[normalizedRegion] || "Unknown";
      } else {
        kontrahentaToRegion[row.NrKontrahenta?.trim()] = "Unknown";
      }
    });

    const monthlyData = Array.from({ length: 12 }, (_, i) => ({ month: monthNames[i] }));

    salesData
      .filter((row) => row.Rok.toString() === selectedYear)
      .forEach((row) => {
        const monthIndex = parseInt(row.Miesiac, 10) - 1;
        const region = kontrahentaToRegion[row.NrKontrahenta?.trim()] || "Unknown";
        const revenue =
          parseFloat(row.K_15) ||
          parseFloat(row.K_19) ||
          parseFloat(row.K_10) ||
          parseFloat(row.K_21) ||
          parseFloat(row.K_17) ||
          parseFloat(row.K_11) ||
          parseFloat(row.K_22) ||
          0;

        if (!monthlyData[monthIndex][region]) {
          monthlyData[monthIndex][region] = 0;
        }
        if (revenue > 0) {
          monthlyData[monthIndex][region] += revenue;
        }
      });

    setFilteredData(monthlyData);
  }, [salesData, clientsData, selectedYear]);

  const toggleRegion = (region) => {
    setHiddenRegions((prevHidden) => {
      const newHidden = new Set(prevHidden);
      if (newHidden.has(region)) newHidden.delete(region);
      else newHidden.add(region);
      return newHidden;
    });
  };

// ---- Color scheme: distinct, clear colors per region ----
const regions = Object.values(regionNameMapping);

// Dedicated colors for each voivodeship (good contrast, fairly colorblind-friendly)
const regionColorMap = {
  mazowieckie: "#2E7D32",          // deep green
  "śląskie": "#1565C0",            // blue
  podkarpackie: "#8E24AA",         // purple
  lubuskie: "#F4511E",             // orange/red
  zachodniopomorskie: "#43A047",   // green
  wielkopolskie: "#FB8C00",        // orange
  dolnośląskie: "#6D4C41",         // brown
  "łódzkie": "#00897B",            // teal
  "świętokrzyskie": "#5E35B1",     // violet
  pomorskie: "#039BE5",            // light blue
  lubelskie: "#C0CA33",            // olive
  opolskie: "#7CB342",             // light green
  podlaskie: "#FDD835",            // yellow
  "warmińsko-mazurskie": "#00ACC1",// cyan
  "kujawsko-pomorskie": "#AB47BC", // magenta
  małopolskie: "#EF5350",          // red
  Unknown: "#9E9E9E",              // neutral grey
};

// Fallback palette if you ever add new regions not in the map
const fallbackColors = [
  "#2E7D32", "#1565C0", "#8E24AA", "#F4511E",
  "#43A047", "#FB8C00", "#6D4C41", "#00897B",
];

const getRegionColor = (region, index) =>
  regionColorMap[region] || fallbackColors[index % fallbackColors.length];


  return (
    <Wrapper>
      <ChartTitle>
        Zmiany sprzedaży w województwach w czasie
        <InfoIconWrapper data-tooltip-id="sales-changes-tooltip">
          <Info />
        </InfoIconWrapper>
      </ChartTitle>
      <InfoTooltip
        id="sales-changes-tooltip"
        place="left"
        content={
          <>
            <strong>Zmiany sprzedaży</strong><br />
            Pokazuje wartość sprzedaży w rozbiciu na miesiące<br />
            dla województw w wybranym roku.
          </>
        }
      />

      <Controls>
        <FilterGroup>
          <label>Rok:</label>
          <Select
            value={selectedYear}
            onChange={(e) => setSelectedYear(e.target.value)}
          >
            {availableYears.map((year) => (
              <option key={year} value={year}>{year}</option>
            ))}
          </Select>
        </FilterGroup>
      </Controls>

      <ResponsiveContainer width="100%" height={700}>
        <LineChart data={filteredData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="month" />
          <YAxis tickFormatter={(val) => val.toLocaleString("pl-PL")} />
          <Tooltip formatter={(val) => (val ?? 0).toLocaleString("pl-PL")} />
          <Legend onClick={(e) => toggleRegion(e.value)} wrapperStyle={{ cursor: "pointer" }} />
{regions.map((region, index) => (
  <Line
    key={region}
    type="monotone"
    dataKey={region}
    stroke={getRegionColor(region, index)}  // 🎨 clear multi-color scheme
    strokeWidth={2}
    dot={{ r: 3 }}
    hide={hiddenRegions.has(region)}
    isAnimationActive={false}
  />
))}

        </LineChart>
      </ResponsiveContainer>
    </Wrapper>
  );
};

export default SalesChangesByRegion;
